/** Hook for fetching items the current user is borrowing. */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { ACTIVE_LOAN_STATUSES } from '@/hooks/useLoanQueryUtils';

export interface BorrowedItem {
  loan_id: string;
  item_id: string;
  item_name: string;
  owner_id: string;
  owner_name: string | null;
  lender_user_id: string;
  borrowed_at: string;
  due_at: string | null;
  status: string;
  return_initiated_by: string | null;
  photo_url: string | null;
  category: string | null;
  group_id: string | null;
  notes: string | null;
}

/**
 * Returns items the current user is actively borrowing.
 *
 * Queries the `loans` table for ACTIVE, RETURN_PENDING, and PENDING_ACCEPTANCE
 * loans where the current user is the borrower, then enriches each record with
 * item details (name, photo, category) and owner profile information. Falls back
 * to base-schema columns and ACTIVE-only status if the primary query fails due
 * to a schema mismatch after a migration.
 *
 * @returns TanStack query result containing an array of {@link BorrowedItem}.
 */
export function useMyBorrowedItems() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['my-borrowed-items', user?.id],
    queryFn: async (): Promise<BorrowedItem[]> => {
      if (!user) return [];

      // 1. Fetch active loans where current user is the borrower
      const { data: loans, error: loansError } = await supabase
        .from('loans')
        .select(
          'id, item_id, borrower_id, lender_user_id, borrowed_at, due_at, status, return_initiated_by, group_id, notes',
        )
        .eq('borrower_id', user.id)
        .in('status', ACTIVE_LOAN_STATUSES)
        .order('borrowed_at', { ascending: false });

      // [LEARN] Schema migration resilience: if new DB columns haven't been deployed yet, the primary
      // query fails. We catch it and retry with only the base columns that are guaranteed to exist.
      let safeLoans = loans ?? [];
      if (loansError) {
        console.warn(
          '[useMyBorrowedItems] Loans query failed, retrying with base columns:',
          loansError.message,
        );
        const { data: fallbackLoans, error: fallbackError } = await supabase
          .from('loans')
          .select('id, item_id, borrower_id, lender_user_id, borrowed_at, due_at, status')
          .eq('borrower_id', user.id)
          .in('status', ['ACTIVE'])
          .order('borrowed_at', { ascending: false });
        if (fallbackError) {
          console.error(
            '[useMyBorrowedItems] Loans fallback query also failed:',
            fallbackError.message,
          );
          throw fallbackError;
        }
        safeLoans = fallbackLoans ?? [];
      }
      if (safeLoans.length === 0) return [];

      // 2. Fetch item details for all loaned items
      const itemIds = [...new Set(safeLoans.map((l) => l.item_id))];
      const { data: items, error: itemsError } = await supabase
        .from('items')
        .select('id, name, photo_url, category, owner_id')
        .in('id', itemIds);

      if (itemsError) throw itemsError;

      // 3. Fetch owner profiles (owner may differ from lender in free-circulation)
      const ownerIds = [...new Set(items?.map((i) => i.owner_id) ?? [])];
      let profileMap = new Map<string, string | null>();

      if (ownerIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, display_name')
          .in('user_id', ownerIds);

        if (!profilesError && profiles) {
          profileMap = new Map(profiles.map((p) => [p.user_id, p.display_name]));
        }
      }

      const itemMap = new Map(items?.map((i) => [i.id, i]) ?? []);

      return safeLoans.map((loan) => {
        const item = itemMap.get(loan.item_id);
        return {
          loan_id: loan.id,
          item_id: loan.item_id,
          item_name: item?.name ?? 'Unknown Item',
          owner_id: item?.owner_id ?? loan.lender_user_id,
          owner_name: profileMap.get(item?.owner_id ?? loan.lender_user_id) ?? null,
          lender_user_id: loan.lender_user_id,
          borrowed_at: loan.borrowed_at,
          due_at: loan.due_at ?? null,
          status: loan.status,
          return_initiated_by: loan.return_initiated_by ?? null,
          photo_url: item?.photo_url ?? null,
          category: item?.category ?? null,
          group_id: loan.group_id ?? null,
          notes: loan.notes ?? null,
        };
      });
    },
    enabled: !!user,
  });
}
